import { createContext, useEffect, useReducer } from 'react';
import axios from 'src/utils/axios';
import { verify, mapTokenToUserDetails } from 'src/utils/jwt';
import PropTypes from 'prop-types';
import { loginUser, registerUser } from 'src/services/auth';
import { capitalize } from 'src/utils/capitalize';

const initialAuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const setSession = (accessToken, user) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common.Authorization;
  }
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REFRESH: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && verify(accessToken)) {
          let user = JSON.parse(window.localStorage.getItem('user'));

          if (!user) {
            user = mapTokenToUserDetails(accessToken);
          }

          setSession(accessToken, user);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };
    console.log('initialize called');

    initialize();
  }, []);

  const login = async (email, password) => {
    const response = await loginUser({
      email,
      password,
    });
    const { accessToken } = response.data;

    const user = mapTokenToUserDetails(accessToken);

    setSession(accessToken, user);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (registerDetails) => {
    await registerUser(registerDetails);
  };

  const refresh = async (user) => {
    const userRole = user.role === 'app-admin' ? 'admin' : 'user';
    const updatedUser = {
      id: user.id,
      name: user.firstname + ' ' + user.lastname,
      email: user.email,
      role: userRole,
      jobtitle: capitalize(userRole),
    };
    const accessToken = localStorage.getItem('accessToken');
    setSession(accessToken, updatedUser);
    dispatch({
      type: 'REFRESH',
      payload: {
        user: updatedUser,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
